import { SupportedLocale } from '@afterpay/types'
import { isCallable, isUndefinedOrEmpty } from '@afterpay/utils'
import clsx from 'clsx'
import React, { ChangeEvent, forwardRef, KeyboardEvent, useLayoutEffect, useRef } from 'react'

import { useThemeClass } from '../Theme'
import { useCombinedRef } from '../useCombinedRef'
import { useIsMobileBrowser } from '../useDevice'
import { noop } from '../utils/function'
import { ALLOWED_KEYS, EMPTY_DISPLAY, VARIANT } from './constants'
import { getDisplayValue, getInputSize } from './helper'
import styles from './style.module.scss'
import { EmptyDisplay, Variant } from './types'

export interface InputMoneyProps extends Omit<React.HTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  currencySymbol?: string
  value?: string
  onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  readOnly?: boolean
  required?: boolean
  tabIndex?: number
  testNameSpace?: string
  maxValue?: number
  maxDigits?: number
  bold?: boolean
  locale?: SupportedLocale
  autoFocus?: boolean
  emptyDisplay?: EmptyDisplay
  variant?: Variant
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-disabled'?: boolean
  'data-dd-action-name'?: string
}

export const InputMoney = forwardRef<HTMLInputElement, InputMoneyProps>(
  (
    {
      currencySymbol = '$',
      value = '',
      onChange = noop,
      onBlur = noop,
      disabled = false,
      placeholder,
      readOnly = false,
      required = false,
      tabIndex = 0,
      testNameSpace = '',
      maxValue,
      maxDigits,
      bold = false,
      locale,
      className,
      autoFocus = false,
      emptyDisplay = EMPTY_DISPLAY.ZERO,
      variant,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-disabled': ariaDisabled,
      'data-dd-action-name': dataDdActionName,
      ...otherProps
    },
    ref
  ) => {
    const themeClass = useThemeClass()
    const innerRef = useRef<HTMLInputElement>(null)
    const combinedRef = useCombinedRef(ref, innerRef)
    const isMobileBrowser = useIsMobileBrowser()
    const cursorRef = useRef<{ sigChars: number } | null>(null)
    const isComposingRef = useRef(false)

    const hasTestId = testNameSpace.length > 0
    const inputTabIndex = disabled ? -1 : tabIndex

    const effectiveValue = isUndefinedOrEmpty(value) && emptyDisplay === EMPTY_DISPLAY.ZERO ? '0' : value
    const displayValue = getDisplayValue(effectiveValue, locale)
    const inputSize = getInputSize(displayValue, placeholder, isMobileBrowser)

    const componentClassNames = clsx(
      styles.component,
      disabled && styles.modDisabled,
      readOnly && styles.modReadOnly,
      bold && styles.modBold,
      styles[themeClass],
      className
    )
    const inputClassNames = clsx(
      styles.input,
      bold && styles.modBold,
      isUndefinedOrEmpty(value) && styles.modEmpty,
      variant === VARIANT.SUCCESS && styles.modSuccess,
      variant === VARIANT.ERROR && styles.modError,
      styles[themeClass]
    )
    const currencyClassNames = clsx(
      styles.currency,
      bold && styles.modBold,
      variant === VARIANT.SUCCESS && styles.modSuccess,
      variant === VARIANT.ERROR && styles.modError,
      styles[themeClass]
    )

    const sanitizeValue = (val: string): string => {
      let sanitized = val.replace(/[^0-9.]/g, '')

      const parts = sanitized.split('.')
      if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('')
      }

      if (parts.length === 2 && parts[1].length > 2) {
        sanitized = parts[0] + '.' + parts[1].slice(0, 2)
      }

      return sanitized
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
      const rawValue = event.target.value
      const selectionStart = event.target.selectionStart
      const selectionEnd = event.target.selectionEnd

      let sanitizedValue = sanitizeValue(rawValue)

      if (sanitizedValue !== '' && sanitizedValue !== '.' && !sanitizedValue.startsWith('0.')) {
        sanitizedValue = sanitizedValue.replace(/^0+/, '') || '0'
      }

      if (typeof maxDigits !== 'undefined' && sanitizedValue !== '' && sanitizedValue !== '.') {
        const integerPart = sanitizedValue.split('.')[0]

        if (integerPart !== '0' && integerPart !== '0.') {
          const withoutLeadingZeros = integerPart.replace(/^0+/, '')
          const digitCount = withoutLeadingZeros.length

          if (digitCount > maxDigits) {
            return
          }
        }
      }

      if (typeof maxValue !== 'undefined' && sanitizedValue !== '' && sanitizedValue !== '.') {
        const numericValue = parseFloat(sanitizedValue)
        if (!isNaN(numericValue) && numericValue > maxValue) {
          sanitizedValue = maxValue.toString()
        }
      }

      if (!isComposingRef.current && selectionStart !== null && selectionStart === selectionEnd) {
        let sigChars = rawValue.slice(0, selectionStart).replace(/[^0-9.]/g, '').length

        if (sanitizedValue === '' && emptyDisplay === EMPTY_DISPLAY.ZERO) {
          sigChars = 1
        }

        cursorRef.current = { sigChars }
      } else {
        cursorRef.current = null
      }

      if (rawValue !== sanitizedValue) {
        event.target.value = sanitizedValue
      }

      if (isCallable(onChange)) {
        onChange(sanitizedValue, event)
      }
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        const input = innerRef.current
        if (input != null && input.selectionStart === input.selectionEnd && input.selectionStart !== null) {
          const pos = input.selectionStart
          const charIndex = event.key === 'Backspace' ? pos - 1 : pos
          if (charIndex >= 0 && charIndex < displayValue.length && !/[0-9.]/.test(displayValue[charIndex])) {
            event.preventDefault()
            const newPos = event.key === 'Backspace' ? pos - 1 : pos + 1
            input.setSelectionRange(newPos, newPos)
            return
          }
        }
      }

      const isNumber = /^[0-9]$/.test(event.key)
      const isAllowedKey = ALLOWED_KEYS.includes(event.key)

      if (event.key === '.') {
        if (value.includes('.')) {
          event.preventDefault()
        }
        return
      }

      if (!isAllowedKey && !isNumber) {
        event.preventDefault()
      }
    }

    const handleBlur = (): void => {
      if (value.endsWith('.')) {
        const cleanedValue = value.slice(0, -1)
        if (innerRef.current != null) {
          innerRef.current.value = cleanedValue
          const event = new Event('change', { bubbles: true })
          innerRef.current.dispatchEvent(event)
        }
      }

      if (isCallable(onBlur)) {
        onBlur()
      }
    }

    useLayoutEffect(() => {
      const input = innerRef.current
      if (cursorRef.current != null && input != null && document.activeElement === input) {
        const { sigChars } = cursorRef.current
        let count = 0
        let pos = 0
        while (pos < displayValue.length && count < sigChars) {
          if (/[0-9.]/.test(displayValue[pos])) {
            count++
          }
          pos++
        }
        input.setSelectionRange(pos, pos)
      }
      cursorRef.current = null
    }, [displayValue])

    return (
      <div className={componentClassNames} {...(hasTestId && { 'data-testid': `${testNameSpace}-wrapper` })}>
        <span className={currencyClassNames} {...(hasTestId && { 'data-testid': `${testNameSpace}-currency` })} aria-hidden='true'>
          {currencySymbol}
        </span>
        <input
          ref={combinedRef}
          type='text'
          inputMode='decimal'
          autoComplete='off'
          autoCorrect='off'
          spellCheck='false'
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onCompositionStart={() => {
            isComposingRef.current = true
          }}
          onCompositionEnd={() => {
            isComposingRef.current = false
          }}
          autoFocus={autoFocus}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          tabIndex={inputTabIndex}
          placeholder={placeholder}
          size={inputSize}
          className={inputClassNames}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-disabled={disabled ?? ariaDisabled}
          data-dd-action-name={dataDdActionName ?? `${testNameSpace} Money Input`}
          {...(hasTestId && { 'data-testid': `${testNameSpace}-money-input` })}
          {...otherProps}
        />
      </div>
    )
  }
)

InputMoney.displayName = 'InputMoney'
